import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import { Box, Typography, Grid } from "@mui/material";
import { ModalContext } from "../../shared/components/EMPModal/ModalContext";
import EMPBanner from "../../shared/components/EMPBanner/EMPBanner";
import CapitalizeText from "../../utils/CapitalizeText";
import { StyledTableRow } from "../../shared/Tables/TableRow";
import RefundPurchaseDeposit from "../../shared/components/EMPModal/ModalFragments/RefundPurchaseDeposit";
import PurchaseDepositDecision from "../../shared/components/EMPModal/ModalFragments/PurchaseDepositDecision";
import {
  getRefundStatus,
  isRefundStatusAccepted,
  isPurchaseDepositStatusAccepted,
  isPurchaseDepositStatusValid,
} from "../../shared/rules/PurchaseDepositRules";
import { useTranslation } from "react-i18next";
import {
  getPaymentType,
  hasCreditApp,
  get3pfCreditAppStatus,
} from "../../shared/rules/FinancingRules";
import {
  PURCHASE_DEPOSIT_STATUS,
  PURCHASE_REFUND_STATUS,
  DOC_UPLOAD_STATUS_COMPLETE,
  DOC_UPLOAD_STATUS_LOCKED,
  DOC_UPLOAD_STATUS_READY,
  PDS_STATUS_ACCEPT,
  PDS_STATUS_READY,
  PDS_STATUS_REJECT,
} from "../../common/Constants";
import UpperCaseText from "../../utils/UpperCaseText";
import { formatPriceValue } from "../../utils/FormatPrice";
import EMPLoader from "../../shared/components/EMPLoader/EMPLoader";
import CreditApplicationTable from "./financing-tables/CreditApplicationTable";
import CashTable from "./financing-tables/CashTable";
import LeaseTable from "./financing-tables/LeaseTable";
import ThirdPartyFinancingTable from "./financing-tables/ThirdPartyFinancingTable";
import { FINANCE_STATUS } from "../../shared/constants/FinanceConstants";
import { getFinancingFieldDisplay } from "../../shared/constants/FinancingConstants";
import { usePurchaseDeposit } from "../../shared/hooks/usePurchaseDeposit";
import { usePurchaseDepositStatus } from "../../shared/hooks/usePurchaseDepositStatus";
import { usePaymentTypeDetails } from "../../shared/hooks/usePaymentTypeDetails";
import { trackReferralExit } from "../../shared/services/analytics/AnalyticsService";
import { UserContext } from "../../shared/context/userContext";
import {
  FinancingCard,
  FinancingDetailsSection,
  getFinancingHeadingSx,
} from "./FinancingPresentation";

const extractPurchaseDeposit = (purchaseDeposit) => {
  if (!purchaseDeposit) {
    return {};
  }

  const {
    depositAmount,
    acceptRejectDealerUserId,
    cardLastFourDigits,
    paymentTransactionId,
    acceptRejectStatus,
  } = purchaseDeposit;

  return {
    depositAmount,
    acceptRejectDealerUserId,
    cardLastFourDigits,
    paymentTransactionId,
    acceptRejectStatus,
  };
};

const extractRefund = (purchaseDeposit) => {
  const currentRefundStatus = getRefundStatus(purchaseDeposit);

  if (!currentRefundStatus || !purchaseDeposit) {
    return {};
  }

  const {
    refundStatus,
    refundAmount,
    refundDate,
    refundTransactionId,
    refundDealerUserId,
  } = purchaseDeposit;

  return {
    refundStatus,
    refundAmount,
    refundDate,
    refundTransactionId,
    refundDealerUserId,
  };
};

const getTranslatedPdsStatus = (systemStatus, t) => {
  switch (systemStatus) {
    case PDS_STATUS_READY:
      return t("ModelEOrders.pdsReadyStatus");
    case PDS_STATUS_ACCEPT:
      return t("ModelEOrders.pdsAcceptedStatus");
    case PDS_STATUS_REJECT:
      return t("ModelEOrders.pdsRejectedStatus");
    default:
      return null;
  }
};

const getTranslatedDocUploadStatus = (docUploadDataStatus, t) => {
  switch (UpperCaseText(docUploadDataStatus)) {
    case DOC_UPLOAD_STATUS_COMPLETE:
      return t("ModelEOrders.complete");
    case DOC_UPLOAD_STATUS_READY:
      return t("ModelEOrders.ready");
    case DOC_UPLOAD_STATUS_LOCKED:
      return t("ModelEOrders.locked");
    default:
      return null;
  }
};

const extractCustomerContracts = (content, t) => {
  const { preDeliverySummary } = content;
  const { docUploadDataStatus } = content.customerReservation;
  const result = {};

  if (preDeliverySummary) {
    result.preDeliverySummary = getTranslatedPdsStatus(
      preDeliverySummary.systemStatus,
      t
    );
    result.dealJacketId = preDeliverySummary.dealJacketId;
  }

  if (docUploadDataStatus) {
    result.docUpload = getTranslatedDocUploadStatus(docUploadDataStatus, t);
  }

  return result;
};

const isEmptyObject = (obj) => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

const formatFinanceValue = (value) => {
  if (value === null || value === undefined) {
    return "--";
  }

  return value;
};

const shortenInvoice = (value) => {
  return value ? value.substr(value.length - 12, value.length) : null;
};

const getStatusKey = (status, statusMap) => {
  for (const [key, value] of Object.entries(statusMap)) {
    if (value === status) {
      return CapitalizeText(key);
    }
  }

  return null;
};

const getPurchaseDepositStatusInfo = (status, t) => {
  switch (status) {
    case PURCHASE_DEPOSIT_STATUS.ACCEPTED:
      return {
        heading: t("financing.purchaseDeposit.fields.0"),
        content: getStatusKey(
          PURCHASE_DEPOSIT_STATUS.ACCEPTED,
          PURCHASE_DEPOSIT_STATUS
        ),
        highlight: true,
        color: "success",
      };
    case PURCHASE_DEPOSIT_STATUS.REJECTED:
    case PURCHASE_DEPOSIT_STATUS.DECLINED:
    case PURCHASE_DEPOSIT_STATUS.FAILED:
      return {
        heading: t("financing.purchaseDeposit.fields.0"),
        content: getStatusKey(status, PURCHASE_DEPOSIT_STATUS),
        highlight: true,
        color: "error",
      };
    default:
      return {
        heading: t("financing.purchaseDeposit.fields.0"),
        content: "Pending",
        highlight: true,
        color: "info",
      };
  }
};

const getRefundStatusInfo = (purchaseDepositData, t) => {
  const status = getRefundStatus(purchaseDepositData);

  switch (status) {
    case PURCHASE_REFUND_STATUS.ACCEPTED:
      return {
        heading: t("financing.refund.fields.0"),
        content: "Refunded",
        highlight: true,
        color: "success",
      };
    case PURCHASE_REFUND_STATUS.REFUNDED_OFFLINE:
      return {
        heading: t("financing.refund.fields.0"),
        content: getStatusKey(
          PURCHASE_REFUND_STATUS.REFUNDED_OFFLINE,
          PURCHASE_REFUND_STATUS
        ),
        highlight: true,
        color: "success",
      };
    case PURCHASE_REFUND_STATUS.DECLINED:
    case PURCHASE_REFUND_STATUS.FAILED:
      return {
        heading: t("financing.refund.fields.0"),
        content: getStatusKey(status, PURCHASE_REFUND_STATUS),
        highlight: true,
        color: "error",
      };
    default:
      return {};
  }
};

const purchaseDepositActionAvailable = (
  purchaseDepositData,
  isOrderRefundEligible
) => {
  const isStatusValid = isPurchaseDepositStatusAccepted(purchaseDepositData);
  const refundStatus = getRefundStatus(purchaseDepositData);

  return isOrderRefundEligible === true && isStatusValid && !refundStatus;
};

// Lincoln has no purchase deposit action card, so the decision the card used to
// offer is started from here once the purchase request has been accepted.
const purchaseDepositDecisionAvailable = (purchaseDepositData, content) => {
  const isPurchaseRequestAccepted =
    content?.customerReservation?.purchaseInfo?.acceptRejectStatus ===
    "accepted";

  if (!isPurchaseRequestAccepted || !purchaseDepositData?.depositAmount) {
    return false;
  }

  return (
    !isPurchaseDepositStatusValid(purchaseDepositData) &&
    !getRefundStatus(purchaseDepositData)
  );
};

const getMonthlyPaymentContent = (monthlyPayment) => {
  if (!monthlyPayment) {
    return "--";
  }

  return `${formatPriceValue(monthlyPayment)} per mo`;
};

const getOptionalPriceContent = (value) => {
  if (!value) {
    return "--";
  }

  return formatPriceValue(value);
};

const createPaymentFieldConfig = (paymentType, t) => [
  {
    key: "financeType",
    condition: (response) => response.financeType,
    heading: () => getFinancingFieldDisplay("financeType"),
    content: (response) => response.financeType,
    additionalProps: () => ({
      hasTooltip: paymentType === "Finance",
      toolTipMessage: t(
        "OrderDetailsConsumerDeposit.paymentMethodTypeTooltipBody"
      ),
    }),
  },
  {
    key: "statusFromPaymentDescription",
    condition: (response) => response.statusFromPaymentDescription,
    heading: () => getFinancingFieldDisplay("status"),
    content: (response) =>
      CapitalizeText(response.statusFromPaymentDescription),
    additionalProps: (response) => ({
      highlight: true,
      color:
        response.statusFromPaymentDescription === "APPROVED"
          ? "success"
          : "info",
    }),
  },
  {
    key: "downPayment",
    condition: (response) =>
      response.downPayment !== null && response.downPayment !== undefined,
    heading: () => getFinancingFieldDisplay("downPayment"),
    content: (response) => formatPriceValue(response.downPayment),
  },
  {
    key: "purchaseDepositAmountInt",
    condition: (response) => response.purchaseDepositAmountInt,
    heading: () => "Purchase Deposit",
    content: (response) => formatPriceValue(response.purchaseDepositAmountInt),
  },
  {
    key: "totalPriceWithAddOns",
    condition: (response) =>
      response.totalPriceWithAddOns && paymentType === "Cash",
    heading: () => "Estimated Amount Due at Signing",
    content: (response) => formatPriceValue(response.totalPriceWithAddOns),
  },
  {
    key: "apr",
    condition: (response) => response.apr,
    heading: () => getFinancingFieldDisplay("apr"),
    content: (response) => `${Number.parseFloat(response.apr).toFixed(2)}%`,
  },
  {
    key: "monthlyPayment",
    condition: (response) => response.monthlyPayment >= 0,
    heading: () => getFinancingFieldDisplay("monthlyPayment"),
    content: (response) => getMonthlyPaymentContent(response.monthlyPayment),
  },
  {
    key: "taxesAndFees",
    condition: (response) =>
      response.taxesAndFees !== null && response.taxesAndFees !== undefined,
    heading: () => "Taxes and Fees",
    content: (response) => formatPriceValue(response.taxesAndFees),
  },
  {
    key: "term",
    condition: (response) => response.term,
    heading: () => getFinancingFieldDisplay("term"),
    content: (response) => `${response.term} months`,
  },
  {
    key: "totalAmountFinancedStandard",
    condition: (response) =>
      response.totalAmountFinancedStandard !== null &&
      response.totalAmountFinancedStandard !== undefined,
    heading: () => getFinancingFieldDisplay("amountFinanced"),
    content: (response) =>
      formatPriceValue(response.totalAmountFinancedStandard),
  },
  {
    key: "baseMonthlyPayment",
    condition: (response) => response.baseMonthlyPayment,
    heading: () => "Base Monthly Payment",
    content: (response) => getOptionalPriceContent(response.baseMonthlyPayment),
  },
  {
    key: "monthlyUseTax",
    condition: (response) => response.monthlyUseTax,
    heading: () => "Monthly Use Tax",
    content: (response) => formatPriceValue(response.monthlyUseTax),
  },
  {
    key: "annualMileage",
    condition: (response) => response.annualMileage,
    heading: () => "Annual Mileage",
    content: (response) =>
      Number(Number.parseFloat(response.annualMileage)).toLocaleString("en"),
  },
  {
    key: "residualData",
    condition: (response) => response.residualData >= 0,
    heading: () => "Residual Data",
    content: (response) => response.residualData || "--",
  },
  {
    key: "capitalizedCostReduction",
    condition: (response) => response.capitalizedCostReduction >= 0,
    heading: () => "Capitalized Cost Reduction",
    content: (response) =>
      getOptionalPriceContent(response.capitalizedCostReduction),
  },
  {
    key: "availableIncentivesAndNonCashCredits",
    condition: (response) => response.availableIncentivesAndNonCashCredits,
    heading: () => "Available Incentives And Non Cash Credits",
    content: (response) =>
      formatPriceValue(response.availableIncentivesAndNonCashCredits),
  },
  {
    key: "tradeInPrice",
    condition: (response) => response.tradeInPrice >= 0,
    heading: () => getFinancingFieldDisplay("tradeInValue"),
    content: (response) => getOptionalPriceContent(response.tradeInPrice),
  },
  {
    key: "cashDueAtSigning",
    condition: (response) => response.cashDueAtSigning >= 0,
    heading: () => "Cash Due At Signing",
    content: (response) => getOptionalPriceContent(response.cashDueAtSigning),
  },
  {
    key: "totalAmountDueAtSigning",
    condition: (response) => response.totalAmountDueAtSigning >= 0,
    heading: () => "Total Amount Due At Signing",
    content: (response) => formatPriceValue(response.totalAmountDueAtSigning),
  },
  {
    key: "dataQuoteTotalAmountDueAtSigning",
    condition: (response) => response.dataQuoteTotalAmountDueAtSigning >= 0,
    heading: () => "Data Quote Total Amount Due At Signing",
    content: (response) =>
      formatPriceValue(response.dataQuoteTotalAmountDueAtSigning),
  },
];

const buildPaymentData = (response, paymentType, t) => {
  const paymentData = {};

  if (response.spotBuy) {
    paymentData.spotBuy = response.spotBuy;
  }

  const fieldConfig = createPaymentFieldConfig(paymentType, t);

  fieldConfig.forEach((field) => {
    if (field.condition(response)) {
      paymentData[field.key] = {
        heading: field.heading(),
        content: field.content(response),
        ...(field.additionalProps ? field.additionalProps(response) : {}),
      };
    }
  });

  return paymentData;
};

const EstimatedAmountAccordion = ({
  paymentData,
  expanded,
  onToggle,
  t,
  isLincolnBrand,
}) => {
  const monthlyPayment = paymentData.monthlyPayment?.content
    ?.replaceAll("per mo", "")
    .trim();

  return (
    <Box gridColumn={{ xs: "span 12" }}>
      <FinancingDetailsSection
        isLincolnBrand={isLincolnBrand}
        expanded={expanded}
        onChange={onToggle}
        heading={t("financing.lease.accordionHeaders.0")}
      >
        <StyledTableRow
          text={t("PurchaseDetails.paymentMethod.firstPayment")}
          data={formatFinanceValue(monthlyPayment)}
        />

        <StyledTableRow
          text={t("PurchaseDetails.paymentMethod.downPayment")}
          indent
          data={formatFinanceValue(paymentData.downPayment?.content)}
        />

        <StyledTableRow
          text={t("PurchaseDetails.paymentMethod.taxesAndFees")}
          indent
          data={formatFinanceValue(paymentData.taxesAndFees?.content)}
        />
      </FinancingDetailsSection>
    </Box>
  );
};

EstimatedAmountAccordion.propTypes = {
  isLincolnBrand: PropTypes.bool,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  paymentData: PropTypes.shape({
    downPayment: PropTypes.shape({
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    monthlyPayment: PropTypes.shape({
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    taxesAndFees: PropTypes.shape({
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
};

const buildCustomerPurchaseDeposit = ({
  purchaseDepositDetails,
  purchaseDepositStatusInfo,
  t,
}) => {
  if (isEmptyObject(purchaseDepositDetails)) {
    return [];
  }

  const data = [];

  if (!isEmptyObject(purchaseDepositStatusInfo)) {
    data.push(purchaseDepositStatusInfo);
  }

  data.push(
    {
      heading: t("financing.purchaseDeposit.fields.1"),
      content: purchaseDepositDetails.depositAmount,
    },
    {
      heading: t("financing.purchaseDeposit.fields.2"),
      content: purchaseDepositDetails.acceptRejectDealerUserId || "--",
    },
    {
      heading: t("financing.purchaseDeposit.fields.3"),
      content:
        shortenInvoice(purchaseDepositDetails.paymentTransactionId) || "--",
    },
    {
      heading: t("financing.purchaseDeposit.fields.4"),
      content: purchaseDepositDetails.cardLastFourDigits || "--",
    }
  );

  return data;
};

const buildRefundPurchaseDeposit = ({ refundDetails, refundStatusInfo, t }) => {
  if (isEmptyObject(refundDetails)) {
    return [];
  }

  const data = [];

  if (!isEmptyObject(refundStatusInfo)) {
    data.push(refundStatusInfo);
  }

  data.push(
    {
      heading: t("financing.refund.fields.1"),
      content: refundDetails.refundAmount,
    },
    {
      heading: t("financing.refund.fields.2"),
      content: refundDetails.refundDate,
    },
    {
      heading: t("financing.refund.fields.3"),
      content: shortenInvoice(refundDetails.refundTransactionId),
    },
    {
      heading: t("financing.refund.fields.4"),
      content: refundDetails.refundDealerUserId,
    },
    { heading: "", content: "" }
  );

  return data;
};

const buildCustomerContractsData = ({ customerContractsDetails, t }) => {
  if (isEmptyObject(customerContractsDetails)) {
    return {};
  }

  return {
    docUpload: {
      heading: t("financing.customerContracts.fields.0"),
      content: customerContractsDetails.docUpload || "--",
    },
    dealJacketId: {
      heading: t("financing.customerContracts.fields.1"),
      content: customerContractsDetails.dealJacketId || "--",
    },
    preDeliverySummary: {
      heading: t("financing.customerContracts.fields.2"),
      content: customerContractsDetails.preDeliverySummary || "--",
    },
  };
};

const SpotBuyMessage = ({ isLincolnBrand }) => (
  <Box gridColumn={{ xs: "span 12" }}>
    <Typography
      variant="h3"
      sx={{ ...getFinancingHeadingSx(isLincolnBrand), mb: 7 }}
    >
      Financing
    </Typography>
    <Typography variant="h5" component="p">
      Financing data is not available for in store purchase requests
    </Typography>
  </Box>
);

SpotBuyMessage.propTypes = {
  isLincolnBrand: PropTypes.bool,
};

const Financing = ({ content, orderId }) => {
  const [paymentLoaded, setPaymentLoaded] = useState(false);
  const [paymentData, setPaymentData] = useState({});
  const { handleModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const purchaseDepositData = usePurchaseDeposit(orderId);
  const purchaseDepositStatus = usePurchaseDepositStatus(orderId);
  const { user } = useContext(UserContext);
  const isLincolnBrand = user?.brand === "L";
  const purchaseDepositDetails = extractPurchaseDeposit(purchaseDepositData);
  const refundDetails = extractRefund(purchaseDepositData);
  const customerContractsDetails = extractCustomerContracts(content, t);
  const customerPaymentDetails = usePaymentTypeDetails(orderId);
  const paymentType = getPaymentType(orderId);
  const userHasCreditApp = hasCreditApp(orderId);
  const [financeAccordionExpanded, setFinanceAccordionExpanded] =
    useState(false);

  const purchaseDepositStatusInfo = getPurchaseDepositStatusInfo(
    purchaseDepositStatus,
    t
  );

  const refundStatusInfo = getRefundStatusInfo(purchaseDepositData, t);

  const customerPurchaseDeposit = buildCustomerPurchaseDeposit({
    purchaseDepositDetails,
    purchaseDepositStatusInfo,
    t,
  });

  const refundPurchaseDeposit = buildRefundPurchaseDeposit({
    refundDetails,
    refundStatusInfo,
    t,
  });

  const customerContractsData = buildCustomerContractsData({
    customerContractsDetails,
    t,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchPaymentType = async () => {
      try {
        const response = await customerPaymentDetails();

        if (!response || !isMounted) {
          return;
        }

        setPaymentLoaded(true);
        setPaymentData(buildPaymentData(response, paymentType, t));
      } catch (error) {
        console.error("Error loading payment type details:", error);
      }
    };

    fetchPaymentType();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, paymentType]);

  const toggleFinanceAccordion = () => {
    setFinanceAccordionExpanded((currentValue) => !currentValue);
  };

  const renderPaymentTable = () => {
    switch (paymentType) {
      case "Cash":
        return (
          <CashTable
            cashPaymentData={paymentData}
            isLincolnBrand={isLincolnBrand}
          />
        );
      case "Lease":
        return (
          <LeaseTable
            leasePaymentData={paymentData}
            t={t}
            isLincolnBrand={isLincolnBrand}
          />
        );
      case "Third Party Financing":
        return (
          <ThirdPartyFinancingTable
            data={paymentData}
            t={t}
            orderId={orderId}
            user={user}
            isLincolnBrand={isLincolnBrand}
          />
        );
      default: {
        // Other Financing shows Taxes and Fees only in the amount-due accordion.
        // eslint-disable-next-line no-unused-vars
        const { taxesAndFees, ...cardData } = paymentData;
        const filteredData =
          paymentType === "Other Financing Options" ? cardData : paymentData;

        return (
          <>
            <Box gridColumn={{ xs: "span 12" }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="h3"
                    sx={getFinancingHeadingSx(isLincolnBrand)}
                    data-testid="payment-type-heading"
                  >
                    {paymentType === "Ford Credit Financing"
                      ? isLincolnBrand
                        ? "Lincoln Credit Financing"
                        : t("financing.paymentType")
                      : paymentType}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box gridColumn={{ xs: "span 12" }}>
              <FinancingCard
                data={filteredData}
                isLincolnBrand={isLincolnBrand}
              />
            </Box>
          </>
        );
      }
    }
  };

  const renderCustomerContracts = () => {
    if (isEmptyObject(customerContractsData)) {
      return null;
    }

    const showRouteOneButton =
      paymentType === "Ford Credit Financing" ||
      paymentType === "Third Party Financing";

    return (
      <>
        <Box
          gridColumn={{ xs: "span 12" }}
          sx={{ mt: isLincolnBrand ? 16 : 2 }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography
                variant="h3"
                sx={getFinancingHeadingSx(isLincolnBrand)}
              >
                {t("financing.customerContracts.heading")}
              </Typography>
            </Grid>
            <Grid item>
              {showRouteOneButton && (
                <Box>
                  <EMPButton
                    href="https://www.routeone.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    buttonType="link"
                    onClick={() =>
                      trackReferralExit(
                        "https://www.routeone.net",
                        orderId,
                        user
                      )
                    }
                  >
                    {t("common.buttonCtas.routeOne")}
                  </EMPButton>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
        <Box gridColumn={{ xs: "span 12" }}>
          <FinancingCard
            data={customerContractsData}
            isLincolnBrand={isLincolnBrand}
            testId="financing-customer-contracts"
          />
        </Box>
      </>
    );
  };

  const renderEstimatedAmountAccordion = () => (
    <EstimatedAmountAccordion
      paymentData={paymentData}
      expanded={financeAccordionExpanded}
      onToggle={toggleFinanceAccordion}
      t={t}
      isLincolnBrand={isLincolnBrand}
    />
  );

  const renderCreditApplicationSection = () => {
    if (!userHasCreditApp) {
      return null;
    }

    return (
      <>
        <CreditApplicationTable
          orderId={orderId}
          paymentType={paymentType}
          isLincolnBrand={isLincolnBrand}
          t={t}
        />
        {paymentType === "Ford Credit Financing" &&
          renderEstimatedAmountAccordion()}
      </>
    );
  };

  const renderLoadedPaymentContent = () => {
    if (paymentData?.spotBuy) {
      return <SpotBuyMessage isLincolnBrand={isLincolnBrand} />;
    }

    return (
      <>
        {renderPaymentTable()}
        {renderCustomerContracts()}

        {paymentType === "Other Financing Options" &&
          renderEstimatedAmountAccordion()}

        {renderCreditApplicationSection()}
      </>
    );
  };

  const renderPaymentContent = () => {
    if (!paymentLoaded) {
      return (
        <Box gridColumn={{ xs: "span 12" }}>
          <EMPLoader loadingMessage="Loading payment data" />
        </Box>
      );
    }

    return renderLoadedPaymentContent();
  };

  const purchaseDepositCardData = getRefundStatus(purchaseDepositData)
    ? refundPurchaseDeposit.concat(customerPurchaseDeposit)
    : customerPurchaseDeposit;

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(12, 1fr)"
      columnGap={4}
      rowGap={4}
      sx={{ mt: isLincolnBrand ? 0 : 5, mb: 11 }}
    >
      {isRefundStatusAccepted(purchaseDepositData) && (
        <Box gridColumn={{ xs: "span 12" }}>
          <EMPBanner
            title={t("common.banners.refundedPurchaseDeposit.title")}
            content={t("common.banners.refundedPurchaseDeposit.content")}
          />
        </Box>
      )}

      {paymentType === "Third Party Financing" &&
        get3pfCreditAppStatus(orderId) === FINANCE_STATUS.INITIATED && (
          <Box gridColumn={{ xs: "span 12" }}>
            <EMPBanner
              title={t("common.banners.autofiStatus.title")}
              content={t("common.banners.autofiStatus.content")}
              hasLink
              linkText={t("common.banners.autofiStatus.linkText")}
              href="https://www.routeone.com"
            />
          </Box>
        )}

      {paymentLoaded && customerPurchaseDeposit.length > 0 && (
        <>
          <Box gridColumn={{ xs: "span 12" }}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography
                  variant="h3"
                  sx={getFinancingHeadingSx(isLincolnBrand)}
                >
                  {t("financing.purchaseDeposit.heading")}
                </Typography>
              </Grid>
              <Grid item>
                {isLincolnBrand &&
                  purchaseDepositDecisionAvailable(
                    purchaseDepositData,
                    content
                  ) && (
                    <Box>
                      <EMPButton
                        testId="purchase-deposit-decision-button"
                        onClick={() =>
                          handleModal(
                            t("common.actions.purchaseDeposit"),
                            <PurchaseDepositDecision orderId={orderId} />
                          )
                        }
                      >
                        {t("common.buttonCtas.openAction", {
                          action: "Deposit",
                        })}
                      </EMPButton>
                    </Box>
                  )}
                {purchaseDepositActionAvailable(
                  purchaseDepositData,
                  content.customerReservation.isOrderRefundEligible
                ) && (
                  <Box>
                    <EMPButton
                      onClick={() =>
                        handleModal(
                          t("common.buttonCtas.refundPurchaseDeposit"),
                          <RefundPurchaseDeposit orderId={orderId} />
                        )
                      }
                    >
                      {t("common.buttonCtas.refundPurchaseDeposit")}
                    </EMPButton>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
          <Box gridColumn={{ xs: "span 12" }}>
            <FinancingCard
              data={purchaseDepositCardData}
              isLincolnBrand={isLincolnBrand}
              testId="financing-purchase-deposit"
            />
          </Box>
        </>
      )}

      {renderPaymentContent()}
    </Box>
  );
};

Financing.propTypes = {
  content: PropTypes.shape({
    customerReservation: PropTypes.shape({
      docUploadDataStatus: PropTypes.string,
      isOrderRefundEligible: PropTypes.bool,
    }).isRequired,
    preDeliverySummary: PropTypes.shape({
      dealJacketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      systemStatus: PropTypes.string,
    }),
  }).isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default Financing;
